/**
 * SiteSettings — edit site configuration, regenerate API key, or delete site.
 *
 * Available at /sites/:id/settings. Provides forms for:
 * - Changing the theme
 * - Regenerating the API key (with confirmation)
 * - Deleting the site (danger zone with confirmation)
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { api } from '../api/client.ts';
import { CopyButton } from '../components/CopyButton.tsx';
import { AVAILABLE_THEMES, type Site } from '../types.ts';

export const SiteSettings = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // -----------------------------------------------------------------------
  // Queries
  // -----------------------------------------------------------------------

  const {
    data: siteResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['site', id],
    queryFn: () => api.sites.get(id!),
    enabled: !!id,
  });

  const site: Site | undefined = siteResponse?.data;

  // -----------------------------------------------------------------------
  // Local form state — initialized once data loads
  // -----------------------------------------------------------------------

  const [themeId, setThemeId] = useState<string | null>(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [regeneratedKey, setRegeneratedKey] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Use local state if the user has edited, otherwise fall back to server data
  const currentThemeId = themeId ?? site?.themeId ?? '';

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------

  const updateMutation = useMutation({
    mutationFn: () =>
      api.sites.update(id!, {
        themeId: currentThemeId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', id] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => api.sites.regenerateApiKey(id!),
    onSuccess: (response) => {
      if (response.success && response.data) {
        setRegeneratedKey(response.data.apiKey);
      }
      setConfirmRegenerate(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.sites.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      navigate('/sites', { replace: true });
    },
  });

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleSettingsSubmit = (e: FormEvent) => {
    e.preventDefault();
    updateMutation.mutate();
  };

  const handleDelete = () => {
    if (deleteConfirmText === site?.subdomain) {
      deleteMutation.mutate();
    }
  };

  // -----------------------------------------------------------------------
  // Loading & error states
  // -----------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="loading-page">
        <div className="spinner spinner--lg" aria-label={t('siteSettings.loading')} />
        <span>{t('siteSettings.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert--error" role="alert">
        {t('siteSettings.loadError', { error: error instanceof Error ? error.message : t('common.error') })}
      </div>
    );
  }

  if (!site) {
    return (
      <div className="alert alert--error" role="alert">
        {t('siteSettings.notFound')}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const hasChanges = themeId !== null && themeId !== site.themeId;

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h2 className="page-header__title">{t('siteSettings.title')}</h2>
          <p className="page-header__description">
            <Trans
              i18nKey="siteSettings.description"
              values={{ subdomain: site.subdomain }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>
        <Link to={`/sites/${id}`} className="btn btn--secondary">
          {t('siteSettings.backToSite')}
        </Link>
      </div>

      {/* Site configuration form */}
      <div className="card mb-6" style={{ maxWidth: '600px' }}>
        <div className="card__header">
          <h3 className="card__title">{t('siteSettings.config.title')}</h3>
        </div>
        <form onSubmit={handleSettingsSubmit}>
          <div className="card__body">
            {updateMutation.error && (
              <div className="alert alert--error" role="alert">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : t('siteSettings.config.updateError')}
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="alert alert--success" role="status">
                {t('siteSettings.config.updateSuccess')}
              </div>
            )}

            <div className="detail-item" style={{ marginBottom: 'var(--space-5)' }}>
              <span className="detail-item__label">{t('siteSettings.config.googleSheet')}</span>
              <span className="detail-item__value font-mono truncate">
                {site.sheetId}
              </span>
              <a
                href={`https://docs.google.com/spreadsheets/d/${site.sheetId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)', display: 'inline-block' }}
              >
                {t('siteSettings.config.openSheet')}
              </a>
            </div>

            <div className="form-group">
              <label htmlFor="settings-themeId" className="form-label">
                {t('siteSettings.config.theme')}
              </label>
              <select
                id="settings-themeId"
                className="form-select"
                value={currentThemeId}
                onChange={(e) => setThemeId(e.target.value)}
              >
                {AVAILABLE_THEMES.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.name} — {theme.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="card__footer"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)' }}
          >
            <button
              type="submit"
              className="btn btn--primary"
              disabled={updateMutation.isPending || !hasChanges}
            >
              {updateMutation.isPending ? (
                <>
                  <span className="spinner" aria-hidden="true" />
                  {t('siteSettings.config.saving')}
                </>
              ) : (
                t('siteSettings.config.saveChanges')
              )}
            </button>
          </div>
        </form>
      </div>

      {/* API Key section */}
      <div className="card mb-6" style={{ maxWidth: '600px' }}>
        <div className="card__header">
          <h3 className="card__title">{t('siteSettings.apiKey.title')}</h3>
        </div>
        <div className="card__body">
          <div className="detail-item mb-4">
            <span className="detail-item__label">{t('siteSettings.apiKey.currentHash')}</span>
            <span className="detail-item__value font-mono truncate">
              {site.apiKeyHash ? `${site.apiKeyHash.slice(0, 16)}...` : t('siteSettings.apiKey.notSet')}
            </span>
          </div>

          {regeneratedKey && (
            <div className="api-key-reveal" style={{ marginBottom: 'var(--space-4)' }}>
              <h3 className="api-key-reveal__title">{t('siteSettings.apiKey.newTitle')}</h3>
              <p className="api-key-reveal__warning">
                {t('siteSettings.apiKey.newWarning')}
              </p>
              <div className="copy-container">
                <span className="copy-container__value">{regeneratedKey}</span>
                <CopyButton value={regeneratedKey} label={t('siteSettings.apiKey.copyKey')} />
              </div>
            </div>
          )}

          {regenerateMutation.error && (
            <div className="alert alert--error" role="alert">
              {regenerateMutation.error instanceof Error
                ? regenerateMutation.error.message
                : t('siteSettings.apiKey.regenerateError')}
            </div>
          )}

          {confirmRegenerate ? (
            <div className="alert alert--warning">
              <div>
                <strong>{t('siteSettings.apiKey.confirmAreYouSure')}</strong>{' '}
                {t('siteSettings.apiKey.confirmMessage')}
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                  <button
                    className="btn btn--danger btn--sm"
                    onClick={() => regenerateMutation.mutate()}
                    disabled={regenerateMutation.isPending}
                    type="button"
                  >
                    {regenerateMutation.isPending ? (
                      <>
                        <span className="spinner" aria-hidden="true" />
                        {t('siteSettings.apiKey.regenerating')}
                      </>
                    ) : (
                      t('siteSettings.apiKey.confirmButton')
                    )}
                  </button>
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => setConfirmRegenerate(false)}
                    type="button"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              className="btn btn--secondary"
              onClick={() => setConfirmRegenerate(true)}
              type="button"
            >
              {t('siteSettings.apiKey.regenerateButton')}
            </button>
          )}
        </div>
      </div>

      {/* Danger zone */}
      <div
        className="card"
        style={{
          maxWidth: '600px',
          borderColor: 'var(--color-danger-500)',
        }}
      >
        <div className="card__header">
          <h3 className="card__title" style={{ color: 'var(--color-danger-600)' }}>
            {t('siteSettings.dangerZone.title')}
          </h3>
        </div>
        <div className="card__body">
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-gray-600)',
              marginBottom: 'var(--space-4)',
            }}
          >
            {t('siteSettings.dangerZone.description')}
          </p>

          {deleteMutation.error && (
            <div className="alert alert--error" role="alert">
              {deleteMutation.error instanceof Error
                ? deleteMutation.error.message
                : t('siteSettings.dangerZone.deleteError')}
            </div>
          )}

          {confirmDelete ? (
            <div>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-gray-700)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <Trans
                  i18nKey="siteSettings.dangerZone.confirmPrompt"
                  values={{ subdomain: site.subdomain }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={site.subdomain}
                  aria-label="Type subdomain to confirm deletion"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button
                  className="btn btn--danger"
                  onClick={handleDelete}
                  disabled={
                    deleteConfirmText !== site.subdomain ||
                    deleteMutation.isPending
                  }
                  type="button"
                >
                  {deleteMutation.isPending ? (
                    <>
                      <span className="spinner" aria-hidden="true" />
                      {t('siteSettings.dangerZone.deleting')}
                    </>
                  ) : (
                    t('siteSettings.dangerZone.deleteButton')
                  )}
                </button>
                <button
                  className="btn btn--secondary"
                  onClick={() => {
                    setConfirmDelete(false);
                    setDeleteConfirmText('');
                  }}
                  type="button"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="btn btn--danger"
              onClick={() => setConfirmDelete(true)}
              type="button"
            >
              {t('siteSettings.dangerZone.deleteInitial')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
